import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import EditMemberModal from './EditMemberModal';
import { getMembershipPriceByString, getMembershipLabel } from '../../utils/membership';
import { useAuth } from '../../contexts/AuthContext';
import { logAuditEntry } from '../../utils/auditLog';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { computeFailedPaymentEntityIds } from '../../utils/paymentFlags';
import { matchesMembershipFilter, MembershipFilterId } from '../../utils/membershipFilter';
import { formatClubDate } from '../../utils/clubTime';
import { isFamilyDependant, expectedRenewalCents } from '@shared/householdRenewal';

// Use Cognito user pool auth for admin operations (updates/deletes require authentication)
const client = generateClient({ authMode: 'userPool' });

type RenewalStatus = 'CURRENT' | 'PENDING_RENEWAL' | 'EXPIRED' | 'LAPSED';

export interface Member {
  id: string;
  memberNumber?: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
  address: string;
  shirtSize: string;
  membershipType: string;
  familyMembers?: Array<{
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    shirtSize: string;
  }>;
  familyPrimary?: boolean;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
  cognitoSub?: string | null;
  paymentReceived: boolean;
  amountPaid?: number;
  codeOfConductAccepted: boolean;
  codeOfConductVerified: boolean;
  renewalStatus?: RenewalStatus;
  membershipExpiryDate?: string;
  membershipYear?: number;
  renewalDate?: string;
  membershipHistory?: any;
  lastLoginAt?: string;
  isSponsor?: boolean;
  businessName?: string;
  sponsorTier?: 'GOLD' | 'SILVER' | 'BRONZE';
  createdAt: string;
  updatedAt: string;
}

const RENEWABLE_STATUSES: RenewalStatus[] = ['PENDING_RENEWAL', 'EXPIRED', 'LAPSED'];

/** True for the auto-generated placeholder assigned when no real email was
 * ever captured — same pattern used for the "No account" table cell and the
 * CSV export. A member with a placeholder email needs Grant Portal Access
 * (which asks the admin for their real email); a member with a genuine
 * email that just never got an account uses Resend Activation instead. */
function isPlaceholderEmail(email: string | null | undefined): boolean {
  return /^noemail-[^@]+@noemail\.tumbyfc\.com$/.test(email || '');
}

function getRenewalStatusBadge(status?: RenewalStatus | null) {
  if (!status) {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  switch (status) {
    case 'CURRENT':
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
          Current
        </span>
      );
    case 'PENDING_RENEWAL':
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
          Pending Renewal
        </span>
      );
    case 'EXPIRED':
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
          Expired
        </span>
      );
    case 'LAPSED':
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-200 text-red-900">
          Lapsed
        </span>
      );
    default:
      return <span className="text-gray-400 text-xs">—</span>;
  }
}

function formatExpiryDate(dateString?: string | null): string {
  if (!dateString) return '—';
  return formatClubDate(dateString, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export type QuickFilter = 'ALL' | 'INDIVIDUAL' | 'FAMILY' | 'SPONSOR' | 'PAYMENT_PENDING';

/**
 * Predicate behind each stat card, so the card's count and the filtered rows
 * can never disagree. INDIVIDUAL/FAMILY/SPONSOR all delegate to
 * `matchesMembershipFilter` (#148) — the single source of truth shared with
 * the membership-type dropdown — so the two UIs can never disagree either.
 * INDIVIDUAL is ADULT or JUNIOR by the age rule (#127), which also covers
 * the legacy 'SINGLE' type (not itself a dropdown option) since age, not
 * the stored type string, decides ADULT vs JUNIOR.
 */
export function matchesQuickFilter(member: Member, filter: QuickFilter): boolean {
  // PENDING applications with an unresolved payment are loaded alongside the
  // verified roster specifically so they can surface under Payment Pending
  // (a real admin follow-up case — e.g. a declined card on a new
  // application) — but they are not yet members of the club roster in any
  // other sense, so every other quick filter excludes them explicitly. Only
  // PAYMENT_PENDING itself considers a member's status irrelevant.
  if (filter !== 'PAYMENT_PENDING' && member.status !== 'VERIFIED') {
    return false;
  }
  switch (filter) {
    case 'INDIVIDUAL':
      // Age-adult or age-junior, but not a family member (grouped, or plain
      // FAMILY type) and not a sponsor's own record — those are their own
      // cards below, covering the same age-adult sponsors/family members
      // that would otherwise double up on INDIVIDUAL too.
      return (
        (matchesMembershipFilter(member, 'ADULT') || matchesMembershipFilter(member, 'JUNIOR'))
        && !matchesMembershipFilter(member, 'FAMILY')
        && !matchesMembershipFilter(member, 'SPONSOR')
      );
    case 'FAMILY':
      return matchesMembershipFilter(member, 'FAMILY');
    case 'SPONSOR':
      return matchesMembershipFilter(member, 'SPONSOR');
    case 'PAYMENT_PENDING':
      return !member.paymentReceived;
    case 'ALL':
    default:
      return true;
  }
}

/**
 * Human-readable label per familyGroupId, derived from the group's primary member
 * (e.g. "Day Family"). Falls back to any member of the group if no primary is flagged,
 * so a data inconsistency degrades to a usable label instead of showing nothing.
 * The UUID remains the stable key — this is display only.
 */
function buildFamilyLabels(members: Member[]): Map<string, string> {
  const labels = new Map<string, string>();
  const fallback = new Map<string, string>();
  for (const m of members) {
    const gid = (m as { familyGroupId?: string }).familyGroupId;
    if (!gid) continue;
    if (m.familyPrimary && m.lastName) {
      labels.set(gid, `${m.lastName} Family`);
    } else if (m.lastName && !fallback.has(gid)) {
      fallback.set(gid, `${m.lastName} Family`);
    }
  }
  for (const [gid, label] of fallback) {
    if (!labels.has(gid)) labels.set(gid, label);
  }
  return labels;
}

/** Clickable column header. Keyboard-operable and announces sort state to screen readers. */
const SortableTh: React.FC<{
  label: string;
  columnKey: SortKey;
  activeKey: SortKey;
  dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
  /** Overrides the tooltip when the sort key differs from the visible label (e.g. Name sorts by surname). */
  sortHint?: string;
}> = ({ label, columnKey, activeKey, dir, onSort, sortHint }) => {
  const isActive = columnKey === activeKey;
  return (
    <th
      scope="col"
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        title={`Sort by ${sortHint ?? label}`}
        className={`group inline-flex items-center gap-1 uppercase tracking-wider rounded focus:outline-none focus:ring-2 focus:ring-fishing-blue ${
          isActive ? 'text-fishing-blue' : 'hover:text-gray-700'
        }`}
      >
        {label}
        <span
          aria-hidden="true"
          className={isActive ? '' : 'text-gray-300 group-hover:text-gray-400'}
        >
          {isActive ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
};

type SortKey =
  | 'memberNumber'
  | 'name'
  | 'email'
  | 'phoneNumber'
  | 'membershipType'
  | 'renewalStatus'
  | 'membershipExpiryDate'
  | 'createdAt'
  | 'lastLoginAt'
  | 'paymentReceived';

/**
 * Sort value for a member on a given column. Returns a string for text columns
 * (compared with localeCompare, numeric-aware so TFC-9 sorts before TFC-10) or a
 * number for dates/booleans. Missing values sort last in ascending order.
 */
function memberSortValue(member: Member, key: SortKey): string | number | null {
  switch (key) {
    case 'memberNumber':
      return member.memberNumber || null;
    case 'name':
      return `${member.lastName ?? ''} ${member.firstName ?? ''}`.trim() || null;
    case 'email':
      return member.email || null;
    case 'phoneNumber':
      return member.phoneNumber || null;
    case 'membershipType':
      return member.membershipType || null;
    case 'renewalStatus':
      return member.renewalStatus || null;
    case 'membershipExpiryDate':
      return member.membershipExpiryDate
        ? new Date(member.membershipExpiryDate + 'T00:00:00').getTime()
        : null;
    case 'createdAt':
      return member.createdAt ? new Date(member.createdAt).getTime() : null;
    case 'lastLoginAt':
      return member.lastLoginAt ? new Date(member.lastLoginAt).getTime() : null;
    case 'paymentReceived':
      return member.paymentReceived ? 1 : 0;
    default:
      return null;
  }
}

function compareMembers(a: Member, b: Member, key: SortKey, dir: 'asc' | 'desc'): number {
  const av = memberSortValue(a, key);
  const bv = memberSortValue(b, key);

  // Missing values always sort last, regardless of direction, so an empty column
  // never pushes blank rows to the top.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  let result: number;
  if (typeof av === 'number' && typeof bv === 'number') {
    result = av - bv;
  } else {
    result = String(av).localeCompare(String(bv), 'en-AU', {
      numeric: true,
      sensitivity: 'base',
    });
  }
  return dir === 'asc' ? result : -result;
}

const MemberManagement: React.FC = () => {
  const { user, userId } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [failedPaymentIds, setFailedPaymentIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [membershipFilter, setMembershipFilter] = useState<MembershipFilterId>('ALL');
  const [renewalStatusFilter, setRenewalStatusFilter] = useState<'ALL' | RenewalStatus>('ALL');
  // Column sorting. Defaults to surname A-Z, matching the original load-time sort.
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Stat-card quick filter. Composed as an extra predicate rather than folded into
  // membershipFilter because two of the cards (Sponsors, Payment Pending) are not
  // membership types at all.
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('ALL');
  // Keeps members of one family adjacent regardless of the active column sort.
  const [groupFamilies, setGroupFamilies] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [renewingMember, setRenewingMember] = useState<Member | null>(null);
  const [renewalAmount, setRenewalAmount] = useState<number>(0);
  const [renewalAdjustmentReason, setRenewalAdjustmentReason] = useState('');
  const [renewalProcessing, setRenewalProcessing] = useState(false);
  // #127 slice 6 (R7, contract point 10) — the server-expected renewal amount
  // (dollars) for the member being renewed, used to default the modal and to
  // require a reason when the admin-entered amount differs.
  const [renewalExpectedDollars, setRenewalExpectedDollars] = useState<number>(0);
  const [renewalFamilyCount, setRenewalFamilyCount] = useState<number>(1);
  const [exporting, setExporting] = useState(false);
  const [bulkRenewing, setBulkRenewing] = useState(false);
  const [resendingActivationId, setResendingActivationId] = useState<string | null>(null);
  const [grantingPortalAccessId, setGrantingPortalAccessId] = useState<string | null>(null);
  // #157 (S1): a list() call can return null for a row that fails to read
  // (missing required field, GraphQL error), rather than throwing. Counts
  // the skipped rows across loadMembers and handleExportCSV so an amber
  // notice can say how many were left out.
  const [unreadableCount, setUnreadableCount] = useState(0);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load verified members (the active roster), plus PENDING applications
      // that have a real payment problem (paymentReceived: false) — these
      // are genuine outstanding-money cases an admin needs to chase (e.g. a
      // declined card on a new application), and belong under the Payment
      // Pending quick filter even though the applicant hasn't been approved
      // yet. A PENDING application with no payment issue is not fetched
      // here at all — those belong in Member Applications, not this roster
      // view, and including every pending application regardless of
      // payment status would change this page's purpose.
      //
      // BUG FOUND 8 Aug 2026: neither query followed nextToken. Amplify
      // Data's list() defaults to a 100-item page with no explicit limit —
      // with 116 real VERIFIED members in production, every stat tile and
      // the table itself silently dropped roughly the last 16 members
      // (undercounting reported by a real user whose own record fell past
      // the cutoff). Both queries now paginate fully, matching the pattern
      // handleExportCSV already used correctly.
      const fetchAllPages = async (filter: Record<string, unknown>): Promise<{ data: any[]; errors: any[]; unreadable: number }> => {
        const all: any[] = [];
        const allErrors: any[] = [];
        let unreadable = 0;
        let nextToken: string | null | undefined;
        do {
          const page: any = await client.models.Member.list({
            filter,
            nextToken: nextToken || undefined,
          });
          // #157 (S1): a row missing a required field comes back as null
          // with a GraphQL error rather than throwing — keep paging and
          // keep the readable rows; one bad row used to abort the whole
          // load (copies the pattern already used in
          // CompetitionDayDashboard/AdminCheckInPanel).
          const items: any[] = page.data || [];
          const readable = items.filter((m) => m && typeof m === 'object');
          unreadable += items.length - readable.length;
          if (page.errors?.length) {
            allErrors.push(...page.errors);
            console.warn('MemberManagement: skipped unreadable member records', page.errors.map((e: any) => e?.path?.join('.')));
          }
          all.push(...readable);
          nextToken = page.nextToken;
        } while (nextToken);
        return { data: all, errors: allErrors, unreadable };
      };

      const [verifiedResult, pendingUnpaidResult] = await Promise.all([
        fetchAllPages({ status: { eq: 'VERIFIED' } }),
        fetchAllPages({ status: { eq: 'PENDING' }, paymentReceived: { eq: false } }),
      ]);

      const result = {
        data: [...verifiedResult.data, ...pendingUnpaidResult.data],
        errors: [...verifiedResult.errors, ...pendingUnpaidResult.errors],
      };
      setUnreadableCount(verifiedResult.unreadable + pendingUnpaidResult.unreadable);

      // #157 (S1): errors here are now exclusively the per-row "unreadable
      // record" errors already counted and logged above (one bad row no
      // longer aborts the whole load) — never surface them as a hard
      // failure. A genuine fetch failure (network/auth) still throws and is
      // caught by the outer try/catch below.
      if (result.data) {
        const sorted = [...result.data].sort((a, b) =>
          `${a.lastName ?? ''} ${a.firstName ?? ''}`.localeCompare(`${b.lastName ?? ''} ${b.firstName ?? ''}`)
        );
        setMembers(sorted as Member[]);
      }

      // Failed_Payment_Flag — paginated global admin read, never N+1 by member.
      try {
        const allPaymentRecords: any[] = [];
        let nextToken: string | null | undefined;
        do {
          const paymentResult = await client.models.PaymentRecord.list({ limit: 500, nextToken });
          allPaymentRecords.push(...(paymentResult.data || []));
          nextToken = paymentResult.nextToken;
        } while (nextToken);
        setFailedPaymentIds(computeFailedPaymentEntityIds(allPaymentRecords));
      } catch (err) {
        console.error('Error loading payment records for Failed_Payment_Flag (non-fatal):', err);
      }
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const escapeCSV = (val: any): string => {
    if (val == null) return '';
    const s = String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const fmtDate = (d: any): string => {
    if (!d) return '';
    try {
      return formatClubDate(d);
    } catch {
      return '';
    }
  };

  const fmtBool = (v: any): string => (v === true ? 'Yes' : v === false ? 'No' : '');

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Fetch ALL members with pagination
      let allMembers: any[] = [];
      let exportUnreadable = 0;
      let nextToken: string | null = null;
      do {
        const result: any = await client.models.Member.list(nextToken ? { nextToken } : {});
        // #157 (S1): same null-row tolerance as loadMembers — skip
        // unreadable rows rather than letting them break the CSV export
        // (escapeCSV already handles missing optional fields; a null row
        // itself is the crash risk).
        const items: any[] = result.data || [];
        const readable = items.filter((m) => m && typeof m === 'object');
        exportUnreadable += items.length - readable.length;
        if (result.errors?.length) {
          console.warn('MemberManagement export: skipped unreadable member records', result.errors.map((e: any) => e?.path?.join('.')));
        }
        allMembers = allMembers.concat(readable);
        nextToken = result.nextToken ?? null;
      } while (nextToken);

      if (exportUnreadable > 0) {
        setUnreadableCount((prev) => Math.max(prev, exportUnreadable));
      }

      if (allMembers.length === 0) {
        showToast('No members to export', 'error');
        setExporting(false);
        return;
      }

      const headers = [
        'Member Number',
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Membership Type',
        'Business Name',
        'Sponsor Tier',
        'Status',
        'Renewal Status',
        'Membership Year',
        'Expiry Date',
        'Amount Paid',
        'Payment Received',
        'Address',
        'Suburb',
        'State',
        'Post Code',
        'Date of Birth',
        'Is Junior',
        'Emergency Contact Name',
        'Emergency Contact Phone',
        'Joined Date',
        'Notes',
      ];

      const rows = allMembers.map((m: any) =>
        [
          escapeCSV(m.memberNumber),
          escapeCSV(m.firstName),
          escapeCSV(m.lastName),
          escapeCSV(m.email),
          escapeCSV(m.phoneNumber),
          escapeCSV(m.membershipType),
          escapeCSV(m.isSponsor ? m.businessName : ''),
          escapeCSV(m.isSponsor ? m.sponsorTier : ''),
          escapeCSV(m.status),
          escapeCSV(m.renewalStatus),
          escapeCSV(m.membershipYear),
          escapeCSV(fmtDate(m.membershipExpiryDate)),
          escapeCSV(m.amountPaid),
          escapeCSV(fmtBool(m.paymentReceived)),
          escapeCSV(m.address),
          escapeCSV(m.suburb),
          escapeCSV(m.state),
          escapeCSV(m.postCode),
          escapeCSV(fmtDate(m.dateOfBirth)),
          escapeCSV(fmtBool(m.isJunior)),
          escapeCSV(m.emergencyContactName),
          escapeCSV(m.emergencyContactPhone),
          escapeCSV(fmtDate(m.joinedDate)),
          escapeCSV(m.notes),
        ].join(',')
      );

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tfc-members-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showToast(`Exported ${allMembers.length} members`, 'success');
    } catch (err: any) {
      showToast('Failed to export members: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleToggleSelect = (memberId: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedMembers.size === filteredMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredMembers.map((m) => m.id)));
    }
  };

  const handleBulkStatusChange = async (newStatus: 'VERIFIED' | 'REJECTED') => {
    if (selectedMembers.size === 0) {
      showToast('Please select members first', 'error');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to change status to ${newStatus} for ${selectedMembers.size} member(s)?`
    );

    if (!confirmed) return;

    try {
      const promises = Array.from(selectedMembers).map((memberId) =>
        client.models.Member.update({
          id: memberId,
          status: newStatus,
        })
      );

      const results = await Promise.all(promises);
      const hasErrors = results.some((r: any) => r.errors && r.errors.length > 0);

      if (hasErrors) {
        showToast('Some updates failed — check console for details', 'error');
        results.forEach((r: any, i: number) => {
          if (r.errors?.length)
            console.error(`Error updating member ${Array.from(selectedMembers)[i]}:`, r.errors);
        });
      } else {
        showToast('Status updated successfully', 'success');
      }

      await loadMembers();
      setSelectedMembers(new Set());
    } catch (err) {
      console.error('Error updating status:', err);
      showToast('Failed to update status', 'error');
    }
  };

  const handleDeleteMember = async (member: Member) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete ${member.firstName} ${member.lastName}? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const result = await client.models.Member.delete({ id: member.id });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      showToast(`${member.firstName} ${member.lastName} has been deleted`, 'success');
      logAuditEntry(user?.email || '', userId || '', {
        actionType: 'MEMBER_DELETED',
        targetEntityType: 'Member',
        targetEntityId: member.id,
        description: `Deleted member ${member.firstName} ${member.lastName} (${member.email})`,
      });
    } catch (error) {
      console.error('Error deleting member:', error);
      showToast('Failed to delete member. Please try again.', 'error');
    }
  };

  const openRenewalModal = (member: Member) => {
    // #127 slice 6 (R7, contract point 10) — expectedRenewalCents needs the
    // member's own family rows (status, DOB) to price a household renewal;
    // the admin list already holds every member, so use those rows (same
    // familyGroupId), no extra query.
    const familyGroupId = (member as { familyGroupId?: string }).familyGroupId;
    const familyRows = familyGroupId
      ? members.filter((m) => (m as { familyGroupId?: string }).familyGroupId === familyGroupId)
      : [member];
    const expectedCents = expectedRenewalCents(member as any, familyRows as any);
    const standardFee = getMembershipPriceByString(member.membershipType);
    const expectedDollars = expectedCents != null ? expectedCents / 100 : standardFee;
    setRenewingMember(member);
    setRenewalAmount(expectedDollars);
    setRenewalExpectedDollars(expectedDollars);
    setRenewalFamilyCount(familyGroupId ? familyRows.filter((r) => r.status === 'VERIFIED').length : 1);
    setRenewalAdjustmentReason('');
  };

  const handleResendActivation = async (member: Member) => {
    const confirmed = window.confirm(
      `Send a new activation email with a fresh temporary password to ${member.firstName} ${member.lastName} (${member.email})?`
    );
    if (!confirmed) return;

    setResendingActivationId(member.id);
    try {
      const result = await (client.mutations as any).resendActivationEmail({
        memberId: member.id,
      });

      const response = parseLambdaResponse(result?.data);
      if (response?.success) {
        showToast(`Activation email resent to ${member.firstName} ${member.lastName}`, 'success');
        logAuditEntry(user?.email || '', userId || '', {
          actionType: 'MEMBER_ACTIVATION_RESENT',
          targetEntityType: 'Member',
          targetEntityId: member.id,
          description: `Resent activation email for ${member.firstName} ${member.lastName} (${member.email})`,
        });
      } else if (response?.alreadyExists) {
        showToast(response.message, 'error');
      } else {
        showToast(response?.error || 'Failed to resend activation email. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error resending activation email:', err);
      showToast('Failed to resend activation email. Please try again.', 'error');
    } finally {
      setResendingActivationId(null);
    }
  };

  const handleGrantPortalAccess = async (member: Member) => {
    const email = window.prompt(
      `${member.firstName} ${member.lastName} has no portal account and no real email on file. Enter their real email to create one:`
    );
    if (!email) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    const confirmed = window.confirm(
      `Create a portal account for ${member.firstName} ${member.lastName} using ${trimmed}, and send them a welcome email with a temporary password?`
    );
    if (!confirmed) return;

    setGrantingPortalAccessId(member.id);
    try {
      const result = await (client.mutations as any).grantPortalAccess({
        memberId: member.id,
        email: trimmed,
      });

      const response = parseLambdaResponse(result?.data);
      if (response?.success) {
        showToast(`Portal access granted to ${member.firstName} ${member.lastName}`, 'success');
        logAuditEntry(user?.email || '', userId || '', {
          actionType: 'PORTAL_ACCESS_GRANTED',
          targetEntityType: 'Member',
          targetEntityId: member.id,
          description: `Granted portal access to ${member.firstName} ${member.lastName} (${trimmed})`,
        });
        await loadMembers();
      } else {
        showToast(response?.error || 'Failed to grant portal access. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error granting portal access:', err);
      showToast('Failed to grant portal access. Please try again.', 'error');
    } finally {
      setGrantingPortalAccessId(null);
    }
  };

  const handleRenewMember = async () => {
    if (!renewingMember) return;
    setRenewalProcessing(true);
    try {
      const result = await (client.mutations as any).renewMember({
        memberId: renewingMember.id,
        amountPaid: renewalAmount,
        paymentAdjustmentReason: renewalAdjustmentReason || null,
      });

      const response = parseLambdaResponse(result?.data);
      if (response?.success) {
        // #127 slice 6 (R7, contract point 10) — a family renewal shows how
        // many family members were renewed; renewedMemberIds is the server's
        // count of rows actually renewed (point 5).
        const renewedCount: number = Array.isArray(response.renewedMemberIds)
          ? response.renewedMemberIds.length
          : 1;
        showToast(
          isFamilyDependant(renewingMember as any) || renewedCount > 1
            ? `Renewed ${renewedCount} family members`
            : `${renewingMember.firstName} ${renewingMember.lastName} renewed successfully`,
          'success'
        );
        logAuditEntry(user?.email || '', userId || '', {
          actionType: 'MEMBER_RENEWED',
          targetEntityType: 'Member',
          targetEntityId: renewingMember.id,
          description: `Renewed membership for ${renewingMember.firstName} ${renewingMember.lastName}`,
        });
        setRenewingMember(null);
        await loadMembers();
        setSelectedMembers(new Set());
      } else {
        showToast(response?.error || 'Failed to renew membership. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error renewing member:', err);
      showToast('Failed to renew membership. Please try again.', 'error');
    } finally {
      setRenewalProcessing(false);
    }
  };

  const handleBulkRenewal = async () => {
    // #127 slice 6 (R7, contract point 10) — bulk renewal skips dependants:
    // they are renewed with their primary, not individually selected here.
    const renewableMembers = filteredMembers.filter(
      (m) =>
        selectedMembers.has(m.id) &&
        m.renewalStatus &&
        RENEWABLE_STATUSES.includes(m.renewalStatus) &&
        !isFamilyDependant(m as any)
    );

    if (renewableMembers.length === 0) {
      showToast('No selected members are eligible for renewal', 'error');
      return;
    }

    const confirmed = window.confirm(
      `Renew ${renewableMembers.length} member(s)? Families are renewed through their primary at the family price.`
    );
    if (!confirmed) return;

    setBulkRenewing(true);
    let successCount = 0;
    let failCount = 0;

    for (const member of renewableMembers) {
      try {
        const familyGroupId = (member as { familyGroupId?: string }).familyGroupId;
        const familyRows = familyGroupId
          ? members.filter((m) => (m as { familyGroupId?: string }).familyGroupId === familyGroupId)
          : [member];
        const expectedCents = expectedRenewalCents(member as any, familyRows as any);
        const amountPaid =
          expectedCents != null ? expectedCents / 100 : getMembershipPriceByString(member.membershipType);
        const result = await (client.mutations as any).renewMember({
          memberId: member.id,
          amountPaid,
        });
        const response = parseLambdaResponse(result?.data);
        if (response?.success) {
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to renew ${member.firstName} ${member.lastName}:`, response?.error);
        }
      } catch (err) {
        failCount++;
        console.error(`Error renewing ${member.firstName} ${member.lastName}:`, err);
      }
    }

    if (failCount === 0) {
      showToast(`Successfully renewed ${successCount} member(s)`, 'success');
    } else {
      showToast(
        `Renewed ${successCount}, failed ${failCount} — check console for details`,
        'error'
      );
    }

    await loadMembers();
    setSelectedMembers(new Set());
    setBulkRenewing(false);
  };

  const familyLabels = buildFamilyLabels(members);

  const sortedMatches = members
    .filter((member) => {
      // #157 (S1): a malformed row can still reach here with an optional
      // field (or even one normally required) missing — null-safe so a
      // missing firstName/lastName/email filters that row out of a text
      // search instead of throwing and blanking the whole table.
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (member.firstName || '').toLowerCase().includes(term) ||
        (member.lastName || '').toLowerCase().includes(term) ||
        (member.email || '').toLowerCase().includes(term);

      const matchesType = matchesMembershipFilter(member, membershipFilter);

      const matchesRenewalStatus =
        renewalStatusFilter === 'ALL' || member.renewalStatus === renewalStatusFilter;

      return (
        matchesSearch &&
        matchesType &&
        matchesRenewalStatus &&
        matchesQuickFilter(member, quickFilter)
      );
    })
    .sort((a, b) => compareMembers(a, b, sortKey, sortDir));

  /**
   * Keeps a family's members adjacent without discarding the active column sort:
   * each family group appears at the position of its highest-sorting member, and the
   * primary leads its own group. Members with no familyGroupId are a group of one, so
   * ungrouped rows keep their sorted position exactly.
   */
  const groupFamilyRows = (rows: Member[]): Member[] => {
    const order: string[] = [];
    const groups = new Map<string, Member[]>();
    for (const m of rows) {
      const key = (m as { familyGroupId?: string }).familyGroupId || m.id;
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key)!.push(m);
    }
    return order.flatMap((key) => {
      const group = groups.get(key)!;
      if (group.length < 2) return group;
      const primaries = group.filter((m) => m.familyPrimary);
      const rest = group.filter((m) => !m.familyPrimary);
      return [...primaries, ...rest];
    });
  };

  const filteredMembers = groupFamilies ? groupFamilyRows(sortedMatches) : sortedMatches;

  // Clicking a card applies just that card's filter and clears the dropdowns, so the
  // number on the card always explains the rows on screen. Clicking the active card clears it.
  const handleQuickFilter = (filter: QuickFilter) => {
    setQuickFilter((prev) => (prev === filter ? 'ALL' : filter));
    setMembershipFilter('ALL');
    setRenewalStatusFilter('ALL');
    setSelectedMembers(new Set());
  };

  // Clicking the active column flips direction; a new column starts ascending.
  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const selectedRenewableCount = filteredMembers.filter(
    (m) =>
      selectedMembers.has(m.id) &&
      m.renewalStatus &&
      RENEWABLE_STATUSES.includes(m.renewalStatus) &&
      !isFamilyDependant(m as any)
  ).length;

  const formatDate = (dateString: string) => {
    return formatClubDate(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div
          role="alert"
          aria-live="assertive"
          className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* #157 (S1): malformed-row tolerance notice. */}
      {unreadableCount > 0 && (
        <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {unreadableCount === 1 ? '1 unreadable record' : `${unreadableCount} unreadable records`} were skipped.
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Member Management</h2>
          <p className="text-sm text-gray-500 mt-1">{members.length} total members</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadMembers}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Summary stats — each card is a filter. Counts are computed with the same
          predicates used to filter, so a card's number always matches its rows. */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {(
          [
            { key: 'ALL', label: 'Total Members', count: members.filter((m) => matchesQuickFilter(m, 'ALL')).length },
            {
              key: 'INDIVIDUAL',
              label: 'Individual Memberships',
              count: members.filter((m) => matchesQuickFilter(m, 'INDIVIDUAL')).length,
            },
            {
              key: 'FAMILY',
              label: 'Family Memberships',
              // Counts family groups, not people — the filtered rows show every member
              // of those families, so the sub-label makes the difference explicit.
              // A sponsor who registered with a family roster counts here too (see
              // matchesQuickFilter's FAMILY case); a sponsor who registered solo does not.
              count: members.filter((m) => matchesQuickFilter(m, 'FAMILY') && m.familyPrimary).length,
              hint: 'families',
            },
            {
              key: 'SPONSOR',
              label: 'Sponsors',
              count: members.filter((m) => matchesQuickFilter(m, 'SPONSOR')).length,
            },
            {
              key: 'PAYMENT_PENDING',
              label: 'Payment Pending',
              count: members.filter((m) => matchesQuickFilter(m, 'PAYMENT_PENDING')).length,
            },
          ] as { key: QuickFilter; label: string; count: number; hint?: string }[]
        ).map((card) => {
          const isActive = quickFilter === card.key;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => handleQuickFilter(card.key)}
              aria-pressed={isActive}
              title={
                isActive && card.key !== 'ALL'
                  ? `Clear the ${card.label} filter`
                  : `Filter by ${card.label}`
              }
              className={`text-left bg-white shadow rounded-lg p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-fishing-blue ${
                isActive
                  ? 'ring-2 ring-fishing-blue bg-blue-50'
                  : 'hover:bg-gray-50 hover:shadow-md'
              }`}
            >
              <div className="text-sm font-medium text-gray-500">{card.label}</div>
              <div className="text-2xl font-bold text-gray-900">
                {card.count}
                {card.hint && (
                  <span className="ml-1 text-xs font-normal text-gray-500">{card.hint}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {quickFilter !== 'ALL' && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <p className="text-sm text-blue-900">
            Showing <strong>{filteredMembers.length}</strong>{' '}
            {filteredMembers.length === 1 ? 'member' : 'members'} matching the selected card.
          </p>
          <button
            type="button"
            onClick={() => setQuickFilter('ALL')}
            className="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Filters and Bulk Actions */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Members</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Membership Type</label>
            <select
              value={membershipFilter}
              onChange={(e) => setMembershipFilter(e.target.value as MembershipFilterId)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              <option value="ALL">All Types</option>
              <option value="ADULT">Adult</option>
              <option value="JUNIOR">Junior</option>
              <option value="FAMILY">Family</option>
              <option value="SPONSOR">Sponsor</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Renewal Status</label>
            <select
              value={renewalStatusFilter}
              onChange={(e) => setRenewalStatusFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              <option value="ALL">All Statuses</option>
              <option value="CURRENT">Current</option>
              <option value="PENDING_RENEWAL">Pending Renewal</option>
              <option value="EXPIRED">Expired</option>
              <option value="LAPSED">Lapsed</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            id="group-families"
            type="checkbox"
            checked={groupFamilies}
            onChange={(e) => setGroupFamilies(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
          />
          <label htmlFor="group-families" className="text-sm text-gray-600">
            Keep families together (a family stays in one block, led by the primary member,
            whichever column you sort by)
          </label>
        </div>

        {selectedMembers.size > 0 && (
          <div className="mt-4 flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium text-gray-900">
              {selectedMembers.size} member(s) selected
            </span>
            <div className="flex space-x-2">
              {selectedRenewableCount > 0 && (
                <button
                  onClick={handleBulkRenewal}
                  disabled={bulkRenewing}
                  className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 disabled:opacity-50"
                >
                  {bulkRenewing ? 'Renewing...' : `Renew Selected (${selectedRenewableCount})`}
                </button>
              )}
              <button
                onClick={() => handleBulkStatusChange('REJECTED')}
                className="px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
              >
                Deactivate Selected
              </button>
              <button
                onClick={() => setSelectedMembers(new Set())}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Members Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadMembers} className="text-fishing-blue hover:text-blue-700">
              Try Again
            </button>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No members found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        selectedMembers.size === filteredMembers.length &&
                        filteredMembers.length > 0
                      }
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
                    />
                  </th>
                  <SortableTh
                    label="Member #"
                    columnKey="memberNumber"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Name"
                    columnKey="name"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    sortHint="surname"
                  />
                  {quickFilter === 'SPONSOR' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                  )}
                  <SortableTh
                    label="Email"
                    columnKey="email"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Phone"
                    columnKey="phoneNumber"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Type"
                    columnKey="membershipType"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Renewal Status"
                    columnKey="renewalStatus"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Expiry Date"
                    columnKey="membershipExpiryDate"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Joined"
                    columnKey="createdAt"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Last Login"
                    columnKey="lastLoginAt"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Payment"
                    columnKey="paymentReceived"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedMembers.has(member.id)}
                        onChange={() => handleToggleSelect(member.id)}
                        className="h-4 w-4 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.memberNumber ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm text-gray-900">
                            {/* Surname is emphasised because the Name column sorts by
                                surname — without this the ordering looks arbitrary when
                                scanning first names. */}
                            {member.firstName}{' '}
                            <span className="font-semibold">{member.lastName}</span>
                            {failedPaymentIds.has(member.id) && (
                              <span
                                className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800"
                                title="Every payment attempt so far has failed"
                              >
                                Payment failed
                              </span>
                            )}
                          </div>
                          {(() => {
                            const gid = (member as { familyGroupId?: string }).familyGroupId;
                            const label = gid ? familyLabels.get(gid) : undefined;
                            if (!label) return null;
                            return (
                              <div className="text-xs text-gray-500">
                                {label}
                                {member.familyPrimary ? ' · primary' : ''}
                              </div>
                            );
                          })()}
                          {member.familyMembers && member.familyMembers.length > 0 && (
                            <div className="text-xs text-gray-500">
                              +{member.familyMembers.length} family member(s)
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {quickFilter === 'SPONSOR' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.isSponsor && member.businessName ? (
                          <div className="text-sm text-gray-900">
                            {member.businessName}
                            {member.sponsorTier && (
                              <span
                                className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  member.sponsorTier === 'GOLD'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : member.sponsorTier === 'SILVER'
                                    ? 'bg-gray-200 text-gray-800'
                                    : 'bg-amber-100 text-amber-800'
                                }`}
                              >
                                {member.sponsorTier}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isPlaceholderEmail(member.email) ? (
                        // Placeholder email — set for anyone with no portal access, not
                        // only juniors (an adult family member who declined/never got
                        // portal access looks identical). Do not assert a cause the data
                        // doesn't actually confirm; isJunior is a separate, real field.
                        <span className="text-sm text-gray-400 italic">
                          No account{member.isJunior ? ' (junior)' : ''}
                        </span>
                      ) : (
                        <div className="text-sm text-gray-900">{member.email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member.phoneNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {member.membershipType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRenewalStatusBadge(member.renewalStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatExpiryDate(member.membershipExpiryDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(member.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.lastLoginAt ? (
                        formatDate(member.lastLoginAt)
                      ) : (
                        <span className="text-gray-400 italic">Never</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.paymentReceived ? (
                        <span className="inline-flex items-center text-sm text-green-600">
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-sm text-yellow-600">
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {member.renewalStatus &&
                          RENEWABLE_STATUSES.includes(member.renewalStatus) &&
                          (isFamilyDependant(member as any) ? (
                            <span
                              className="text-sm text-gray-500"
                              data-testid={`renews-with-primary-${member.memberNumber}`}
                            >
                              Renews with primary
                            </span>
                          ) : (
                            <button
                              onClick={() => openRenewalModal(member)}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              Renew
                            </button>
                          ))}
                        {member.status === 'VERIFIED' && !member.cognitoSub && !isPlaceholderEmail(member.email) && (
                          <button
                            onClick={() => handleResendActivation(member)}
                            disabled={resendingActivationId === member.id}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            title="No portal account exists yet for this member — send a new activation email with a temporary password"
                          >
                            {resendingActivationId === member.id ? 'Sending…' : 'Resend Activation'}
                          </button>
                        )}
                        {member.status === 'VERIFIED' && !member.cognitoSub && isPlaceholderEmail(member.email) && (
                          <button
                            onClick={() => handleGrantPortalAccess(member)}
                            disabled={grantingPortalAccessId === member.id}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            title="This member has no real email on file (a family/sponsor registration bug, fixed 8 Aug 2026, dropped it) — supply their real email to create a portal account"
                          >
                            {grantingPortalAccessId === member.id ? 'Sending…' : 'Grant Portal Access'}
                          </button>
                        )}
                        <button
                          onClick={() => setEditingMember(member)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteMember(member)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Member Modal */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          isOpen={!!editingMember}
          familyLabel={
            (editingMember as { familyGroupId?: string }).familyGroupId
              ? familyLabels.get((editingMember as { familyGroupId?: string }).familyGroupId!)
              : undefined
          }
          allMembers={members as any}
          onFamilyChanged={async (message) => {
            await loadMembers();
            setEditingMember(null);
            showToast(message, 'success');
          }}
          onClose={() => setEditingMember(null)}
          onSave={async () => {
            await loadMembers();
            setEditingMember(null);
            showToast('Member details updated successfully', 'success');
          }}
        />
      )}

      {/* Renewal Confirmation Modal */}
      {renewingMember && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => !renewalProcessing && setRenewingMember(null)}
            />
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="renew-membership-title"
            >
              <h3 id="renew-membership-title" className="text-lg font-bold text-gray-900 mb-4">Renew Membership</h3>

              <div className="space-y-4">
                {/* Member details */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Member</span>
                    <span className="text-sm font-medium text-gray-900">
                      {renewingMember.firstName} {renewingMember.lastName}
                    </span>
                  </div>
                  {renewingMember.memberNumber && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Member Number</span>
                      <span className="text-sm font-medium text-gray-900">
                        {renewingMember.memberNumber}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Membership Type</span>
                    <span className="text-sm font-medium text-gray-900">
                      {getMembershipLabel(renewingMember.membershipType)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Standard Fee</span>
                    <span className="text-sm font-medium text-gray-900">
                      ${getMembershipPriceByString(renewingMember.membershipType).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Current Status</span>
                    <span>{getRenewalStatusBadge(renewingMember.renewalStatus)}</span>
                  </div>
                </div>

                {/* #127 slice 6 (R7, contract point 10) — family renewal expected amount */}
                {(renewingMember as { familyGroupId?: string }).familyGroupId && (
                  <p
                    className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2"
                    data-testid="renewal-expected"
                  >
                    Family renewal: {renewalFamilyCount} people, expected ${renewalExpectedDollars.toFixed(2)}
                  </p>
                )}

                {/* Amount to pay */}
                <div>
                  <label htmlFor="renewal-amount" className="block text-sm font-medium text-gray-700 mb-1">
                    Amount to Pay ($)
                  </label>
                  <input
                    id="renewal-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={renewalAmount}
                    onChange={(e) => setRenewalAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>

                {/* Adjustment reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adjustment Reason (optional)
                  </label>
                  <select
                    value={
                      renewalAdjustmentReason.startsWith('Other')
                        ? 'Other'
                        : renewalAdjustmentReason
                    }
                    onChange={(e) => setRenewalAdjustmentReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  >
                    <option value="">No adjustment</option>
                    <option value="Committee member">Committee member</option>
                    <option value="Sponsor discount">Sponsor discount</option>
                    <option value="Financial hardship">Financial hardship</option>
                    <option value="Volunteer discount">Volunteer discount</option>
                    <option value="Family discount">Family discount</option>
                    <option value="Promotional offer">Promotional offer</option>
                    <option value="Other">Other</option>
                  </select>
                  {renewalAdjustmentReason.startsWith('Other') && (
                    <input
                      type="text"
                      placeholder="Enter reason..."
                      value={
                        renewalAdjustmentReason.startsWith('Other: ')
                          ? renewalAdjustmentReason.slice(7)
                          : ''
                      }
                      onChange={(e) => {
                        const text = e.target.value.trim();
                        setRenewalAdjustmentReason(text ? `Other: ${text}` : 'Other');
                      }}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                    />
                  )}
                </div>

                {/* #127 slice 6 (R7, contract point 10) — a different amount
                    requires a reason before the Renew button is enabled. The
                    adjustment-reason select above is a separate, optional
                    note; this field specifically explains the amount
                    difference and is required whenever the amount differs
                    from the expected amount. */}
                {Math.round(renewalAmount * 100) !== Math.round(renewalExpectedDollars * 100) && (
                  <div>
                    <label
                      htmlFor="renewal-adjustment-reason-required"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Reason for a different amount *
                    </label>
                    <input
                      id="renewal-adjustment-reason-required"
                      type="text"
                      value={renewalAdjustmentReason}
                      onChange={(e) => setRenewalAdjustmentReason(e.target.value)}
                      aria-required="true"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The amount differs from the expected ${renewalExpectedDollars.toFixed(2)}.
                      Please give a reason.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setRenewingMember(null)}
                  disabled={renewalProcessing}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRenewMember}
                  disabled={
                    renewalProcessing ||
                    (Math.round(renewalAmount * 100) !== Math.round(renewalExpectedDollars * 100) &&
                      !renewalAdjustmentReason.trim())
                  }
                  title={
                    Math.round(renewalAmount * 100) !== Math.round(renewalExpectedDollars * 100) &&
                    !renewalAdjustmentReason.trim()
                      ? 'Give a reason for the different amount before renewing'
                      : undefined
                  }
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {renewalProcessing ? 'Processing...' : 'Renew'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberManagement;
